import type { FC } from "react";
import useIsClamped from "@/hooks/useIsClamped";
import { TEXT } from "@/constants/text";
import styles from "@/pages/CampaignOverviewPage/components/TopPerformerTile.module.css";

interface TopPerformerTileProps {
  // True while the tile's report is loading — the store still holds the
  // previous selection's rows then, so showing a value would be stale.
  isLoading: boolean;
  isSelected: boolean;
  label: string;
  // null while the tile has no qualifying leader (no data, or nothing over
  // the minimum-impressions bar).
  value: string | null;
  onSelect: () => void;
}

// Names longer than this wrap in the large value size, so they drop to the
// smaller one instead.
const LONG_VALUE_LENGTH = 12;
const NO_VALUE = "—";

const TOP_PERFORMERS_TEXT = TEXT.campaignOverview.topPerformers;

const TopPerformerTile: FC<TopPerformerTileProps> = ({
  isLoading,
  isSelected,
  label,
  value,
  onSelect,
}) => {
  const isLong = (value?.length ?? 0) > LONG_VALUE_LENGTH;
  const { isClamped, ref: valueRef } = useIsClamped<HTMLSpanElement>(
    isLoading ? null : value
  );
  const valueClassName = isLong
    ? `${styles.value} ${styles.valueLong}`
    : styles.value;

  return (
    <button
      aria-busy={isLoading}
      aria-pressed={isSelected}
      className={isSelected ? `${styles.tile} ${styles.selected}` : styles.tile}
      type="button"
      onClick={onSelect}
    >
      <span className={styles.label}>{label}</span>
      {isLoading ? (
        <>
          <span className={styles.valueSkeleton} />
          <span className={styles.linkSkeleton} />
        </>
      ) : (
        <>
          <span className={styles.valueWrap}>
            <span ref={valueRef} className={valueClassName}>
              {value ?? NO_VALUE}
            </span>
            {isClamped && (
              <span aria-hidden="true" className={styles.tooltip}>
                <span className={styles.tooltipLabel}>{label}:</span> {value}
              </span>
            )}
          </span>
          <span className={styles.link}>
            {isSelected
              ? TOP_PERFORMERS_TEXT.showingDetail
              : TOP_PERFORMERS_TEXT.viewDetail}
          </span>
        </>
      )}
    </button>
  );
};

export default TopPerformerTile;
